import React, { useId, useRef, useState } from "react";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  BookOpenCheck,
  FileCheck2,
  LayoutDashboard,
  ShieldCheck,
  UsersRound,
  Workflow,
} from "lucide-react";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
import { PRODUCT_STORY_SCENES, productStoryCropFor } from "./productStoryContent";
import { ProductEvidenceOverview, productEvidenceForGuide } from "./productEvidence";
import "./BuyerGuideVisualStage.css";

const SCENE_BY_ID = Object.freeze(Object.fromEntries(PRODUCT_STORY_SCENES.map((scene) => [scene.id, scene])));
const TAB_NAMES = Object.freeze(["Overview", "Workflow", "Outcome"]);

const output = (title, subtitle, panels, trail) => Object.freeze({ title, subtitle, panels, trail });
const panel = (title, label, rows) => Object.freeze({ title, label, rows });
const row = (label, value, status = "Recorded") => Object.freeze({ label, value, status });

export const GUIDE_VISUAL_SPECS = Object.freeze({
  "assignment-notices": Object.freeze({
    sceneId: "communication",
    sceneContext: "Communication half of the guide: audience-scoped notices and channel-specific delivery evidence. Assignment work remains teacher-recorded in the example outcome.",
    title: "From assigned work to a reviewed school update",
    output: output("Assignment & notice workbench", "Teacher-owned work and audience-specific communication remain separate, reviewable records.", [
      panel("Assignment queue", "Teaching record", [
        row("Class 8A · Science", "Sample work · due date recorded", "Open"),
        row("Submission status", "Teacher-recorded received / not received", "Review"),
        row("Grade or note", "Added when the school workflow uses it", "Optional"),
      ]),
      panel("Notice publish", "Communication record", [
        row("Audience", "Parents · Class 8A", "Selected"),
        row("Channel", "In-app plus configured provider", "Configured"),
        row("Evidence", "Attempted, delivered or read where supported", "Review"),
      ]),
    ], ["Prepare work", "Record progress", "Review audience", "Publish update"]),
  }),
  "student-management": Object.freeze({
    sceneId: "parent-student",
    sceneContext: "Family-side outcome of a connected student record. Homework statuses shown in the visual are school-recorded; it does not imply learner self-upload.",
    title: "A usable student record across the school lifecycle",
    output: output("Student lifecycle record", "One authorised record connects allocation, documents, identity and progression.", [
      panel("Student profile", "Sample learner", [
        row("Admission reference", "School-issued sample field", "Recorded"),
        row("Class allocation", "Class 8 · Section A", "Current"),
        row("Guardian context", "Permission-scoped contact record", "Protected"),
      ]),
      panel("Lifecycle evidence", "Authorised teams", [
        row("Documents", "Required record set", "Reviewed"),
        row("Identity", "School ID and reference fields", "Ready"),
        row("Promotion", "Next-year decision and history", "Review"),
      ]),
    ], ["Admit", "Allocate", "Maintain", "Progress"]),
  }),
  "academic-management": Object.freeze({
    sceneId: "academics",
    sceneContext: "Academic workspace context: timetable, attendance, assignments and result approval shown together.",
    title: "Plan teaching and carry evidence into published outcomes",
    output: output("Academic coordination view", "Timetable work and result review use connected academic structures.", [
      panel("Weekly timetable", "Class 8A · Sample", [
        row("Monday · Period 01", "Science · Assigned teacher", "Scheduled"),
        row("Tuesday · Period 03", "Mathematics · Assigned room", "Scheduled"),
        row("Clash review", "Teacher, room and period constraints", "Clear"),
      ]),
      panel("Result review", "Selected term", [
        row("Marks entry", "Subject records complete", "Review"),
        row("Attendance evidence", "School-recorded summary", "Included"),
        row("Publication", "Authorised release only", "Pending"),
      ]),
    ], ["Structure", "Schedule", "Record", "Publish"]),
  }),
  "admission-management": Object.freeze({
    sceneId: "admissions",
    sceneContext: "Admissions workspace context: enquiry stages, document review and authorised student conversion.",
    title: "Keep the enquiry-to-student hand-off accountable",
    output: output("Admissions pipeline", "The sample shows six governed pipeline states and an authorised conversion into the linked student record.", [
      panel("Six-stage pipeline", "Sample application", [
        row("New → Contacted", "Owner and follow-up recorded", "Active"),
        row("Visit Scheduled → Application", "Visit context and application record", "Review"),
        row("Admitted / Lost", "Authorised stage outcome", "Controlled"),
      ]),
      panel("Student conversion", "Accepted record", [
        row("Class & section", "Selected during authorised conversion", "Recorded"),
        row("Documents", "Permitted records retain their review state when carried forward", "Carried forward"),
        row("Conversion", "Student reference created once", "Confirmed"),
      ]),
    ], ["Capture", "Qualify", "Decide", "Convert"]),
  }),
  "fees-management": Object.freeze({
    sceneId: "fee-finance",
    sceneContext: "Fee and finance context: invoice-led collection, provider evidence and reconciliation boundaries.",
    title: "Follow invoice, receipt and provider evidence into reconciliation review",
    output: output("Invoice, receipt and reconciliation", "Synthetic fee records show the evidence chain without displaying a real payment.", [
      panel("School invoice", "Sample learner invoice", [
        row("Demand", "School-configured fee line", "Posted"),
        row("Concession", "Authorised adjustment evidence", "Reviewed"),
        row("Balance", "Calculated from recorded entries", "Current"),
      ]),
      panel("Receipt & provider evidence", "Sample collection", [
        row("Receipt", "Sequential school reference", "Issued"),
        row("Provider reference", "Configured gateway response", "Recorded"),
        row("Reconciliation", "Settlement remains a separate review", "Pending"),
      ]),
    ], ["Invoice", "Collect", "Receipt", "Reconcile"]),
  }),
  "employee-payroll": Object.freeze({
    sceneId: "hr-payroll",
    sceneContext: "HR and payroll context for schools where the still-rolling-out workspace is enabled.",
    title: "Connect workforce records to reviewed pay outcomes",
    output: output("Employee, leave and payroll workspace", "A permission-scoped employee record carries approved inputs into a controlled payroll run.", [
      panel("Employee record", "Sample employee", [
        row("Role & department", "School-assigned context", "Current"),
        row("Documents", "Authorised workforce record", "Reviewed"),
        row("Attendance evidence", "Not an automatic salary decision", "Input"),
      ]),
      panel("Leave & payroll", "Still rolling out", [
        row("Leave request", "Reviewer decision recorded", "Approved"),
        row("Payroll draft", "Maker prepares supported inputs", "Draft"),
        row("Release", "Authorised reviewer confirms", "Controlled"),
      ]),
    ], ["Maintain", "Approve", "Prepare", "Release"]),
  }),
  "alumni-management": Object.freeze({
    title: "Keep a useful, permission-aware alumni directory",
    nativeOverview: Object.freeze([
      ["Directory", "Searchable cohort and profile fields"],
      ["Events", "Staff-maintained basic event records"],
      ["RSVP", "Staff-recorded attendee entry for the selected event"],
      ["Export", "Authorised directory export"],
    ]),
    output: output("Alumni directory and events", "A narrow shipped scope for cohort records, basic events, staff-managed RSVP records and authorised export.", [
      panel("Alumni directory", "Sample cohort", [
        row("Sample alumnus", "Batch 2022 · Bengaluru", "Current"),
        row("Professional field", "Organisation and role if recorded", "Optional"),
        row("Contact visibility", "Permission-scoped fields", "Protected"),
      ]),
      panel("Event & RSVP", "Sample reunion", [
        row("Event record", "Staff-maintained date, venue and description", "Maintained"),
        row("Cohort context", "Staff-selected directory filter", "Recorded"),
        row("RSVP", "Staff-recorded attendee entry", "Recorded"),
      ]),
    ], ["Link graduate", "Maintain directory", "Maintain event", "Record RSVP"]),
  }),
  "holistic-progress-card": Object.freeze({
    sceneId: "academics",
    sceneContext: "Academic record context for the guide: the conceptual frame shows connected attendance, assessment and result-review work. The exact Holistic Progress Card renderer output is kept separately in Outcome.",
    evidenceId: "holistic-progress-card",
    title: "Review a configurable holistic progress record",
    output: output("Holistic progress card", "The report-card example keeps marks, attendance, co-scholastic ratings and remarks under authorised review.", [
      panel("Progress card", "Sample learner · Selected term", [
        row("Scholastic record", "School-recorded subject evidence", "Ready"),
        row("Co-scholastic domains", "School-configured ratings", "Reviewed"),
        row("Attendance", "Recorded term summary", "Included"),
      ]),
      panel("Draft & publication", "Teacher review", [
        row("Teacher remark", "Human-authored or reviewed draft", "Review"),
        row("AI assistance", "Optional draft; never auto-publishes", "Optional"),
        row("Report card", "Authorised PDF publication", "Pending"),
      ]),
    ], ["Configure", "Record", "Review", "Publish"]),
  }),
  "apaar-readiness": Object.freeze({
    title: "Prepare student records and consent evidence for APAAR",
    nativeOverview: Object.freeze([
      ["Readiness", "Deterministic supported-field checks"],
      ["Consent", "Purpose-specific evidence ledger"],
      ["Correction", "Reviewable school-record fixes"],
      ["Import", "Post-government-generation ID import"],
    ]),
    output: output("APAAR readiness, consent and import", "The example separates school preparation from the government process that generates the identifier.", [
      panel("Readiness & consent", "Sample matched record", [
        row("Supported checks", "DOB format, guardian fields and missing-PEN checks", "Review"),
        row("Consent evidence", "Purpose and recorded status", "Logged"),
        row("Correction export", "Authorised school review file", "Ready"),
      ]),
      panel("Government boundary", "Explicit product limit", [
        row("APAAR generation", "Outside Vidyapeeth360", "External"),
        row("Live registry verification", "Not claimed", "Unavailable"),
        row("Generated ID import", "Preview before school-record update", "Controlled"),
      ]),
    ], ["Check", "Record consent", "Correct", "Import generated ID"]),
  }),
});

export const guideVisualSpecFor = (key) => GUIDE_VISUAL_SPECS[key] || null;

export function buyerGuidePanelTransition({ reduced = false, paused = false, mode = "pointer" } = {}) {
  const instant = reduced || paused || mode === "keyboard";
  return {
    initial: instant ? false : { opacity: 0, transform: "translate3d(0,8px,0)" },
    animate: { opacity: 1, transform: "translate3d(0,0,0)" },
    transition: { duration: instant ? 0 : 0.16, ease: [0.22, 1, 0.36, 1] },
  };
}

function ProductWindow({ outputSpec, guideKey }) {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-line bg-canvas"
      data-testid={`buyer-guide-output-${guideKey}`}
      data-source-type="code-native-outcome-map"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface px-4 py-3 sm:px-5">
        <span className="flex min-w-0 items-center gap-2.5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent" aria-hidden="true"><LayoutDashboard className="h-4 w-4" /></span>
          <span className="min-w-0"><strong className="block text-[13.5px] font-semibold text-ink">{outputSpec.title}</strong><small className="mt-0.5 block text-[12px] text-ink-subtle">Vidyapeeth Demo School · synthetic example</small></span>
        </span>
        <span className="rounded-full border border-line bg-muted px-3 py-1.5 text-[12px] font-semibold text-ink-2">Illustrative view</span>
      </header>

      <div className={`grid gap-0 ${outputSpec.panels.length > 2 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
        {outputSpec.panels.map((item) => (
          <article key={item.title} className="min-w-0 border-b border-line p-4 last:border-b-0 lg:border-b-0 lg:border-r lg:last:border-r-0 sm:p-5">
            <p className="text-[12px] font-bold uppercase tracking-[0.11em] text-accent">{item.label}</p>
            <h3 className="mt-1.5 font-heading text-[17px] font-semibold text-ink">{item.title}</h3>
            <dl className="mt-4 border-t border-line">
              {item.rows.map((itemRow, rowIndex) => (
                <div key={`${itemRow.label}-${rowIndex}`} className="grid min-w-0 gap-1 border-b border-line py-3 sm:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] sm:gap-3">
                  <dt className="text-[12px] font-semibold text-ink">{itemRow.label}</dt>
                  <dd className="min-w-0 text-[12px] leading-relaxed text-ink-2">
                    {itemRow.value}
                    <span className="mt-1 block text-[12px] font-bold uppercase tracking-[0.06em] text-accent">{itemRow.status}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>

      <footer className="border-t border-line bg-surface px-4 py-4 sm:px-5">
        <ol className="grid gap-2 sm:grid-cols-4" aria-label={`${outputSpec.title} example progression`}>
          {outputSpec.trail.map((step, index) => (
            <li key={step} className="flex min-w-0 items-center gap-2 text-[12px] font-semibold text-ink-2">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-line bg-muted text-[12px] font-bold text-accent" aria-hidden="true">{index + 1}</span>
              <span className="min-w-0">{step}</span>
              {index < outputSpec.trail.length - 1 ? <ArrowRight className="ml-auto hidden h-3.5 w-3.5 shrink-0 text-ink-subtle sm:block" aria-hidden="true" /> : null}
            </li>
          ))}
        </ol>
      </footer>
    </div>
  );
}

function NativeOverview({ spec, guideKey }) {
  const Icon = guideKey === "alumni-management" ? UsersRound : ShieldCheck;
  return (
    <div
      className="grid overflow-hidden rounded-2xl border border-line bg-canvas lg:grid-cols-[0.42fr_1.58fr]"
      data-testid={`buyer-guide-native-overview-${guideKey}`}
      data-source-type="code-native-context"
    >
      <div className="border-b border-line bg-surface p-5 lg:border-b-0 lg:border-r sm:p-6">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-accent/10 text-accent" aria-hidden="true"><Icon className="h-5 w-5" /></span>
        <p className="mt-5 text-[12px] font-bold uppercase tracking-[0.12em] text-accent">Sample data · not a customer screen</p>
        <h3 className="mt-2 font-heading text-[21px] font-semibold leading-tight text-ink">{spec.title}</h3>
        <p className="mt-3 text-[13px] leading-relaxed text-ink-2">No unrelated product-story image is used for this guide. The view is built from the reviewed shipped scope.</p>
      </div>
      <ol className="grid sm:grid-cols-2">
        {spec.nativeOverview.map(([label, description], index) => (
          <li key={label} className="min-w-0 border-b border-line p-5 odd:sm:border-r [&:nth-last-child(-n+2)]:sm:border-b-0">
            <span className="text-[12px] font-bold tabular-nums tracking-[0.1em] text-ink-subtle">{String(index + 1).padStart(2, "0")}</span>
            <strong className="mt-2 block text-[14px] font-semibold text-ink">{label}</strong>
            <p className="mt-1 text-[12.5px] leading-relaxed text-ink-2">{description}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

const storyDetailPlacement = (orbitSlot) => {
  if (orbitSlot === "left") return "middle-left";
  if (orbitSlot === "right") return "middle-right";
  return orbitSlot;
};

function StoryDetailCrop({ scene, fragment, guideKey, index }) {
  const { crop } = fragment;
  const cropMedia = productStoryCropFor(scene, fragment);
  const placement = storyDetailPlacement(fragment.orbitSlot);

  return (
    <div
      className="buyer-guide-story-detail"
      data-testid={`buyer-guide-story-detail-${guideKey}-${fragment.id}`}
      data-source-type="illustrative-product-story-detail"
      data-orbit-placement={placement}
    >
      <span className="buyer-guide-story-detail__connector" aria-hidden="true" />
      <figure className="buyer-guide-story-detail__window min-w-0 overflow-hidden rounded-xl border border-line bg-surface">
        <div
          className="relative overflow-hidden bg-canvas"
          style={{ aspectRatio: `${cropMedia.width} / ${cropMedia.height}` }}
          data-crop={`${crop.x},${crop.y},${crop.w},${crop.h}`}
          data-crop-source={scene.image}
        >
          {/* PERF-9/VIS-1: the product-story ladder ships an AVIF sibling for every rung, so this
              guide stage negotiates on type exactly like the homepage hero and /modules showcase.
              Without the <source> it silently fetched the WebP rung and the AVIF encode — 25-30%
              smaller at the same quality — was generated for nothing on this route. `contents`
              keeps <picture> boxless so the surrounding crop framing is unchanged. */}
          <picture className="contents">
            <source type="image/avif" srcSet={cropMedia.avifSrcSet} sizes="147px" />
            <img
              src={cropMedia.image}
              srcSet={cropMedia.srcSet}
              sizes="147px"
              alt=""
              aria-hidden="true"
              width={cropMedia.width}
              height={cropMedia.height}
              loading="lazy"
              decoding="async"
              draggable="false"
              className="pointer-events-none block h-auto w-full select-none object-contain [filter:none] [image-rendering:auto] [transform:none] transition-none"
              data-image-treatment="illustrative-detail-crop"
            />
          </picture>
        </div>
        <figcaption className="buyer-guide-story-detail__caption border-t border-line bg-surface px-3 py-2.5 text-[12px] font-semibold leading-snug text-ink-2">
          <span className="buyer-guide-story-detail__index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
          <span>{fragment.label}</span>
        </figcaption>
      </figure>
    </div>
  );
}

function StoryOverview({ scene, spec, guideKey }) {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-line bg-canvas"
      data-testid={`buyer-guide-story-overview-${guideKey}`}
      data-source-type="illustrative-product-story-gallery"
      data-theme-policy="surrounding-chrome"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-surface px-4 py-3 sm:px-5">
        <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.1em] text-accent"><BookOpenCheck className="h-4 w-4" aria-hidden="true" /> Connected school workbench</span>
        <span className="text-[12px] font-semibold text-ink-subtle">One complete view · three linked focus windows</span>
      </div>
      <div
        className="buyer-guide-story-workbench"
        data-entry-motion="panel-transform-opacity-160ms"
        data-detail-max-css-width="147"
      >
        <figure className="buyer-guide-story-main min-w-0 overflow-hidden rounded-xl border border-line bg-surface" data-source-type="illustrative-product-story-full-frame">
          <figcaption className="buyer-guide-story-main__bar border-b border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-ink-2">
            <span className="buyer-guide-story-main__lights" aria-hidden="true"><i /><i /><i /></span>
            <span>Complete conceptual workspace</span>
            <span className="buyer-guide-story-main__role">Main screen</span>
          </figcaption>
          <picture className="contents">
            <source type="image/avif" srcSet={scene.imageAvifSrcSet} sizes="(max-width: 760px) 92vw, 1120px" />
            <img
              src={scene.image}
              srcSet={scene.imageSrcSet}
              sizes="(max-width: 760px) 92vw, 1120px"
              alt={scene.imageAlt}
              width={scene.imageWidth}
              height={scene.imageHeight}
              loading="lazy"
              decoding="async"
              draggable="false"
              className="block h-auto w-full max-w-full object-contain [filter:none]"
              data-testid={`buyer-guide-story-image-${guideKey}`}
              data-image-treatment="full-unfiltered-uncropped"
            />
          </picture>
        </figure>
        <div className="buyer-guide-story-popouts" aria-label={`${spec.title} linked visual details`}>
          {scene.visualFragments.map((fragment, index) => (
            <StoryDetailCrop key={fragment.id} scene={scene} fragment={fragment} guideKey={guideKey} index={index} />
          ))}
        </div>
      </div>
      <p className="border-t border-line bg-surface px-4 py-3 text-[12.5px] leading-relaxed text-ink-2 sm:px-5">{spec.sceneContext}</p>
    </div>
  );
}

function OutcomeOverview({ spec, guideKey, evidence }) {
  return (
    <div
      className={evidence ? "grid items-start gap-3 xl:grid-cols-[minmax(0,1.08fr)_minmax(22rem,0.92fr)]" : "grid gap-3"}
      data-testid={`buyer-guide-outcome-overview-${guideKey}`}
      data-outcome-media={evidence ? "illustrative-plus-first-party" : "illustrative-only"}
    >
      <ProductWindow outputSpec={spec.output} guideKey={guideKey} />
      {evidence ? (
        <div className="buyer-guide-outcome-evidence" data-source-type="first-party-product-evidence" data-testid={`buyer-guide-outcome-evidence-${guideKey}`}>
          <ProductEvidenceOverview evidence={evidence} testId={`buyer-guide-product-evidence-${guideKey}`} />
        </div>
      ) : null}
    </div>
  );
}

function WorkflowOverview({ guide }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-canvas" data-testid="buyer-guide-visual-workflow">
      <header className="flex items-center gap-3 border-b border-line bg-surface px-4 py-4 sm:px-5">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent" aria-hidden="true"><Workflow className="h-5 w-5" /></span>
        <span><strong className="block text-[14px] font-semibold text-ink">{guide.title} operating passage</strong><small className="mt-0.5 block text-[12px] text-ink-subtle">Each stage stays connected to its reviewed capability scope.</small></span>
      </header>
      <ol className="grid sm:grid-cols-2">
        {(guide.workflow || []).map((stage, index) => (
          <li key={`${stage.title}-${index}`} className="grid min-w-0 grid-cols-[32px_minmax(0,1fr)] gap-3 border-b border-line p-4 odd:sm:border-r [&:nth-last-child(-n+2)]:sm:border-b-0 sm:p-5">
            <span className="grid h-8 w-8 place-items-center rounded-full border border-line bg-surface text-[12px] font-bold text-accent" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
            <span className="min-w-0"><strong className="block text-[13.5px] font-semibold text-ink">{stage.title}</strong><span className="mt-1.5 block text-[12.5px] leading-relaxed text-ink-2">{stage.description}</span></span>
          </li>
        ))}
      </ol>
    </div>
  );
}

const TAB_ICONS = Object.freeze([LayoutDashboard, Workflow, FileCheck2]);

export default function BuyerGuideVisualStage({ guide }) {
  const spec = guideVisualSpecFor(guide?.key);
  const scene = spec?.sceneId ? SCENE_BY_ID[spec.sceneId] : null;
  const evidence = productEvidenceForGuide(guide?.key);
  const prefersReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [focusIndex, setFocusIndex] = useState(0);
  const [activationMode, setActivationMode] = useState("pointer");
  const tabRefs = useRef([]);
  const rawId = useId();
  const id = rawId.replace(/:/g, "");

  if (!guide || !spec) return null;

  const activate = (index, mode) => {
    setActivationMode(mode);
    setFocusIndex(index);
    setActiveIndex(index);
  };
  const focusTab = (index) => {
    const next = (index + TAB_NAMES.length) % TAB_NAMES.length;
    setActivationMode("keyboard");
    setFocusIndex(next);
    tabRefs.current[next]?.focus();
  };
  const onTabKeyDown = (event, index) => {
    if (event.key === "ArrowRight") { event.preventDefault(); focusTab(index + 1); }
    else if (event.key === "ArrowLeft") { event.preventDefault(); focusTab(index - 1); }
    else if (event.key === "Home") { event.preventDefault(); focusTab(0); }
    else if (event.key === "End") { event.preventDefault(); focusTab(TAB_NAMES.length - 1); }
    else if (event.key === "Enter" || event.key === " ") { event.preventDefault(); activate(index, "keyboard"); }
  };

  const transition = buyerGuidePanelTransition({
    reduced: Boolean(prefersReducedMotion),
    paused: Boolean(paused),
    mode: activationMode,
  });
  const activeName = TAB_NAMES[activeIndex];
  const showingStoryImage = activeIndex === 0 && Boolean(scene);
  const showingNativeContext = activeIndex === 0 && !scene;
  const showingWorkflow = activeIndex === 1;
  const showingOutcomeEvidence = activeIndex === 2 && Boolean(evidence);

  return (
    <section
      className="border-y border-line bg-muted/25"
      aria-labelledby={`buyer-guide-visual-heading-${id}`}
      data-testid="buyer-guide-visual-stage"
      data-guide-visual-key={guide.key}
      data-motion-paused={paused || prefersReducedMotion ? "true" : "false"}
      data-aira-fab-clearance="hide-mobile"
    >
      <div className="mx-auto max-w-7xl px-5 py-14 sm:py-16">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <div className="max-w-3xl">
            <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-accent">Guided product passage</p>
            <h2 id={`buyer-guide-visual-heading-${id}`} className="mt-2 font-heading text-[28px] font-bold leading-tight tracking-tight text-ink sm:text-[35px]">See the workflow and what it produces.</h2>
            <p className="mt-3 text-[14px] leading-relaxed text-ink-2">Move manually between a visual product-story context, the reviewed workflow and a route-specific outcome. Where a current renderer or route capture exists, Outcome keeps it beside the clearly illustrative summary.</p>
          </div>
          <div
            role="tablist"
            aria-label={`${guide.title} illustrative views`}
            className="grid grid-cols-3 gap-1 rounded-2xl border border-line bg-surface p-1.5"
            data-testid="buyer-guide-visual-tabs"
          >
            {TAB_NAMES.map((name, index) => {
              const Icon = TAB_ICONS[index];
              const selected = index === activeIndex;
              return (
                <button
                  key={name}
                  ref={(node) => { tabRefs.current[index] = node; }}
                  type="button"
                  role="tab"
                  id={`buyer-guide-visual-tab-${id}-${index}`}
                  aria-selected={selected}
                  aria-controls={`buyer-guide-visual-panel-${id}`}
                  tabIndex={focusIndex === index ? 0 : -1}
                  onFocus={() => setFocusIndex(index)}
                  onPointerDown={() => setActivationMode("pointer")}
                  onClick={(event) => activate(index, event.detail === 0 ? "keyboard" : "pointer")}
                  onKeyDown={(event) => onTabKeyDown(event, index)}
                  className={`inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-[12.5px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${activationMode === "keyboard" || paused || prefersReducedMotion ? "transition-none" : "transition-[background-color,color,transform] duration-150"} ${selected ? "bg-primary text-primary-fg" : "text-ink-2 hover:bg-muted hover:text-ink"}`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" /> <span>{name}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-7 rounded-[26px] border border-line bg-surface p-2 shadow-[0_28px_80px_-58px_rgb(var(--vp-fg-default)/0.5)] sm:p-3">
          <m.div
            key={`${guide.key}-${activeName}`}
            {...transition}
            id={`buyer-guide-visual-panel-${id}`}
            role="tabpanel"
            aria-labelledby={`buyer-guide-visual-tab-${id}-${activeIndex}`}
            tabIndex={0}
            className="min-h-[24rem] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/55"
            data-testid="buyer-guide-visual-panel"
            data-active-view={activeName.toLowerCase()}
          >
            {activeIndex === 0 && (scene
              ? <StoryOverview scene={scene} spec={spec} guideKey={guide.key} />
              : <NativeOverview spec={spec} guideKey={guide.key} />)}
            {activeIndex === 1 ? <WorkflowOverview guide={guide} /> : null}
            {activeIndex === 2 ? <OutcomeOverview spec={spec} guideKey={guide.key} evidence={evidence} /> : null}
          </m.div>

          <div className="mt-2 flex items-start gap-2.5 rounded-2xl border border-line bg-canvas px-4 py-3 text-[12px] leading-relaxed text-ink-2" data-testid="buyer-guide-visual-disclosure">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
            <p>
              <strong className="font-semibold text-ink">
                {showingStoryImage
                  ? "Illustrative product-story gallery · synthetic sample data · not a live customer screen"
                  : showingNativeContext
                  ? "Code-native context · based on reviewed shipped scope · no customer data"
                  : showingWorkflow
                  ? "Reviewed workflow narrative · capability-scoped · no customer data"
                  : showingOutcomeEvidence
                  ? "Illustrative outcome map + first-party product evidence · deterministic synthetic fixtures · no customer data"
                  : "Code-native illustrative outcome · not live product UI · no customer data"}
              </strong>{" "}
              {showingStoryImage
                ? "The complete product-story frame is shown unfiltered, with three labelled crops from that same frame. It explains related context rather than claiming to be a live or exact product screen; theme selection changes only the surrounding website chrome."
                : showingNativeContext
                ? "No unrelated raster is used for this guide. The context view is built from the reviewed capability boundary and uses synthetic labels only."
                : showingWorkflow
                ? "These stages explain the reviewed operating passage; actual availability still depends on the guide’s stated plan, provider and rollout conditions."
                : showingOutcomeEvidence
                ? evidence.themePolicy === "document-light"
                  ? "The outcome map is illustrative. Beside it, the exact output was produced by the shipped renderer, is visibly marked SAMPLE / DEMO, contains no actionable QR, and stays white because it is a print preview."
                  : "The outcome map is illustrative. Beside it, the exact product view was captured from the shipped route with a deterministic fixture and switches between genuine light and dark captures without CSS filtering or pixel recolouring."
                : "This route-specific outcome uses synthetic labels to explain reviewed scope; it is not customer proof or a live product session."}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
